import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FacebookIcon from "@tsesco/Icons/FacebookIcon";
import InstagramLogo from "@tsesco/Icons/InstagramLogo";
import LinkdinIcon from "@tsesco/Icons/LinkdinIcon";
import TwitterIcon from "@tsesco/Icons/TwitterIcon";
import YouTubeIcon from "@tsesco/Icons/YouTubeIcon";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    facebookdestination = "facebookdestination",
    twitterdestination = "twitterdestination",
    linkdindestination = "linkdindestination",
    youtubedestination = "youtubedestination",
    instagramdestination = "instagramdestination",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.facebookdestination]: LinkFieldValue;
        [fields.twitterdestination]: LinkFieldValue;
        [fields.linkdindestination]: LinkFieldValue;
        [fields.youtubedestination]: LinkFieldValue;
        [fields.instagramdestination]: LinkFieldValue;
    };
}

export interface NewsLetterStyles {
    mainWrapper?: InjectableCss;
    iconHoverEffect?: InjectableCss;
}

export const newsStyle: NewsLetterStyles = {
    mainWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 10px;
                    `,
                    css`
                        margin-top: 10px;
                        display: flex;
                        justify-content: space-evenly;
                    `,
                    css`
                        margin-top: 10px;
                        display: flex;
                        justify-content: space-evenly;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    iconHoverEffect: {
        css: css`
            svg:hover path {
                fill: ${getColor("primary")};
                cursor: pointer;
            }
        `,
    },
};

const styles = newsStyle;

export const FooterSocialIcons: FC<OwnProps> = ({ fields }) => {
    const facebookUrl = useGetLink(fields.facebookdestination);
    const twitterUrl = useGetLink(fields.twitterdestination);
    const linkdinUrl = useGetLink(fields.linkdindestination);
    const youtubeUrl = useGetLink(fields.youtubedestination);
    const instagramUrl = useGetLink(fields.instagramdestination);

    const addToFbDataLayer = () => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "social_media_interaction",
                ecommerce: {
                    icon_name: "facebook",
                },
            });
        }
        return dataLayer;
    };

    const addToTwDataLayer = () => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "social_media_interaction",
                ecommerce: {
                    icon_name: "twitter",
                },
            });
        }
        return dataLayer;
    };

    const addToLNDataLayer = () => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "social_media_interaction",
                ecommerce: {
                    icon_name: "linkedin",
                },
            });
        }
        return dataLayer;
    };

    const addToYtDataLayer = () => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "social_media_interaction",
                ecommerce: {
                    icon_name: "youtube",
                },
            });
        }
        return dataLayer;
    };
    const addToIgDataLayer = () => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "social_media_interaction",
                ecommerce: {
                    icon_name: "instagram",
                },
            });
        }
        return dataLayer;
    };

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <Link
                rel="nofollow"
                id="fb-icon"
                onClick={addToFbDataLayer}
                href={facebookUrl.url}
                target="_blank"
                {...styles.iconHoverEffect}
            >
                <FacebookIcon />
            </Link>
            <Link
                rel="nofollow"
                id="tw-icon"
                onClick={addToTwDataLayer}
                href={twitterUrl.url}
                target="_blank"
                {...styles.iconHoverEffect}
            >
                <TwitterIcon />
            </Link>
            <Link
                rel="nofollow"
                id="linkedin-icon"
                onClick={addToLNDataLayer}
                href={linkdinUrl.url}
                target="_blank"
                {...styles.iconHoverEffect}
            >
                <LinkdinIcon />
            </Link>
            <Link
                rel="nofollow"
                id="youtube-icon"
                onClick={addToYtDataLayer}
                href={youtubeUrl.url}
                target="_blank"
                {...styles.iconHoverEffect}
            >
                <YouTubeIcon />
            </Link>
            <Link
                rel="nofollow"
                id="instagram-icon"
                onClick={addToIgDataLayer}
                href={instagramUrl.url}
                target="_blank"
                {...styles.iconHoverEffect}
            >
                <InstagramLogo />
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterSocialIcons,
    definition: {
        group: "Basic",
        displayName: "SE Footer Social Icons",
        fieldDefinitions: [
            {
                name: fields.facebookdestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },

            {
                name: fields.twitterdestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },

            {
                name: fields.linkdindestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },

            {
                name: fields.youtubedestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.instagramdestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
